import { makeAutoObservable } from 'mobx';
import { environment } from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { PaymentMethodType } from 'core/enums/payment-method';
import { Account, Quote } from 'core/models/account/amount-to-pay.dto';
import { MakePaymentPayloadDTO, PaymentTransactionType } from 'core/models/account/make-payment-payload.dto';
import { Country } from 'core/models/country';
import { Product } from 'core/models/product/product';
import { DirectLinkPaymentService } from 'core/services/api/payment/direct-payment';
import { DPPaymentService } from 'core/services/api/payment/payment-dpp';
import { MomentService as MomentPaymentService } from 'core/services/api/payment/payment-moment';
import { MomentCheckoutPaymentService } from 'core/services/api/payment/payment-moment-checkout';
import { PayAtPaymentService } from 'core/services/api/payment/payment-pay-at';
import { PayAtLinkPaymentService } from 'core/services/api/payment/payment-pay-at-link';
import { PSHPaymentService } from 'core/services/api/payment/payment-psh';
import { RapydPaymentService } from 'core/services/api/payment/payment-rapyd';
import { StorageHelper } from 'core/services/storage';
import { CurrencyStore } from 'core/stores/currency.store';
import { PaymentMetaData } from 'payment/payment.context';
import { Util } from 'utils/common.utils';
import { ProductUtils } from 'utils/product.utils';
import { MomentMakePaymentRequest } from './interfaces/moment-make-payment';
import { PaymentService } from './interfaces/payment-service';
import { RequestRapydCheckout } from './interfaces/request-rapyd-checkout';
import { PaymentPollingStore } from './payment-polling.store';
import { GeneratePaymentRequest } from './payment-request';

export enum PaymentPlatform {
  DPP = 'DPP',
  PayAtExternal = 'PayAtExternal',
  PayAtRedirect = 'PayAtRedirect',
  RapydRedirect = 'RapydRedirect',
  RapydEmbedded = 'RapydEmbedded',
  PaymentRequestDirectLink = 'PaymentRequestDirectLink',
  PaymentRequestMoment = 'PaymentRequestMoment',
  PaymentRequestMomentCheckout = 'PaymentRequestMomentCheckout',
}

export enum PaymentStatus {
  None = 'None',
  Start = 'Start',
  Success = 'Success',
  PartialSuccess = 'PartialSuccess',
  Failure = 'Failure',
  Pending = 'Pending',
  Created = 'Created',
  Timeout = 'Timeout'
}

export class PaymentStore {
  init = false;
  countryName!: string;
  countryCode!: string;
  customerNumber!: string;
  currencyCode!: string;
  lang!: string;
  pollingStore!: PaymentPollingStore;
  paymentMethod!: PaymentMethodType;
  currencyStore!: CurrencyStore;

  get status() {
    return this.pollingStore?.status;
  }

  get baseUserConfig() {
    return {
      countryName: this.countryName,
      customerNumber: this.customerNumber,
      currencyCode: this.currencyCode,
      countryCode: this.countryCode,
      lang: this.lang,
    };
  }

  get platform() {
    return this.paymentPlatform;
  }

  //private
  paymentService!: PaymentService<MakePaymentPayloadDTO | RequestRapydCheckout | MomentMakePaymentRequest>;
  private paymentPlatform!: PaymentPlatform;

  constructor() {
    makeAutoObservable(this);
  }

  initialize(
    countryName: string,
    countryCode: string,
    customerNumber: string,
    currencyStore: CurrencyStore,
    lang: string,
    paymentPlatform: PaymentPlatform
  ) {
    this.changeCountryName(countryName);
    this.changeCountryCode(countryCode);
    this.changeCustomerNumber(customerNumber);
    this.changeLang(lang);
    this.changePaymentPlatform(paymentPlatform);
    this.setPaymentService();
    this.pollingStore = new PaymentPollingStore(this.paymentService, countryName, customerNumber);
    this.currencyStore = currencyStore;
    this.changeCurrencyCode(this.currencyStore.currency.code);
    this.init = true;
  }

  changePaymentService(PaymentService: PaymentService<MakePaymentPayloadDTO | RequestRapydCheckout | MomentMakePaymentRequest>) {
    this.paymentService = PaymentService;
    this.pollingStore = new PaymentPollingStore(this.paymentService, this.countryName, this.customerNumber);
  }

  changePaymentPlatform(paymentPlatform: PaymentPlatform) {
    this.paymentPlatform = paymentPlatform;
  }

  changeCountryName(countryName: string) {
    this.countryName = countryName;
  }

  changeCountryCode(countryCode: string) {
    this.countryCode = countryCode;
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCurrencyCode(currencyCode: string) {
    this.currencyCode = currencyCode;
  }

  changeLang(lang: string) {
    this.lang = lang;
  }

  changePaymentMethod(method: PaymentMethodType) {
    this.paymentMethod = method;
  }

  initiatePayment(request: Partial<MakePaymentPayloadDTO>, tokenize?: boolean) {
    return tokenize ? this.paymentService.TokenLinks(request) : this.paymentService.MakePayment(request);
  }

  returnUrl(location: string = '', state: string) {
    if (location?.length >= 250) {
      const alpha2CountryCode = Country.GetFromThreeCharCode(this.countryCode)?.alpha2 || null;
      // invalid url length
      return Util.AddQueryParams(environment.DSTV_REDIRECT_URL_BASE.replace('{culture}', `${this.lang}-${alpha2CountryCode}`), 'payRes', state)
    }

    return Util.AddQueryParams(location, 'payRes', state)
  }

  makePayment(requestObject: Partial<MakePaymentPayloadDTO>, tokenize?: boolean, onError?: (err: any) => void) {
    const getReturnUrl = (path: string, existingValue?: string) => {
      if (!!existingValue) {
        return existingValue;
      }

      return this.returnUrl(requestObject.location, path);
    }

    const requestBase = {
      ...requestObject,
      ...this.baseUserConfig,
      failureReturnUrl: getReturnUrl('failure', requestObject?.failureReturnUrl),
      successReturnUrl: getReturnUrl('success', requestObject.successReturnUrl),
      backUrl: getReturnUrl('success', requestObject?.backUrl),
      paymentMethod: this.paymentMethod,
    };

    const baseReq = new GeneratePaymentRequest(
      this.paymentPlatform,
      requestBase,
      this.currencyStore
    );

    return baseReq
      .buildRequest(tokenize)
      .then((request) => {
        return this.initiatePayment(request, tokenize);
      })
      .then(
        (result) => {
          this.setPaymentContext({ ...requestBase, ...result, status: PaymentStatus.Start });

          return baseReq.request
            .handleResponse(result, requestObject.paymentAttribution)
            .then((res) => {
              if (res) {
                return res;
              }
              return;
            });
        },
        (error) => {
          const errorData = error?.response?.data;
          console.error('Failed to initialise payment; ', errorData ?? error);

          if (!error?.length) {
            this.setPaymentContext({ requestBase, ...(errorData || {}), status: PaymentStatus.Failure })

            if (onError) {
              //Not ideal but in dev the Promise.reject doesn't seem to be caught properly, possibly due to Strict mode
              onError(errorData);
              return;
            }

            return Promise.reject(errorData);
          }

          // TODO: Move retry logic into it's own polling service
          const [response, request] = error;
          if (response.status === 408 && this.paymentService.canRefetchPoll) {
            return this.paymentService.RefetchPayment(request?.orderId).then(
              (result) => {
                this.setPaymentContext({ ...requestBase, ...result, status: PaymentStatus.Start })

                return baseReq.request
                  .handleResponse(result, requestObject.paymentAttribution)
                  .then((res) => {
                    if (res) {
                      return res;
                    }
                    return;
                  });
              },
              (error) => {
                console.error('Failed to refetch payment; ', error);
                this.setPaymentContext({ requestBase, error, status: PaymentStatus.Failure })
                return Promise.reject(error);
              }
            );
          }
          this.setPaymentContext({ requestBase, error, status: PaymentStatus.Failure })
          return Promise.reject(error);
        }
      );
  }

  payQuote(quote: Quote, amount: string | number, location: string, paymentMetaData: PaymentMetaData, amountToPayOverride?: string) {
    return this.makePayment({
      accountNumber: quote?.financialAccountNumber
        ? `${quote.financialAccountNumber}`
        : quote.accountNumber
          ? `${quote.accountNumber}`
          : undefined,
      accountBalance: `${quote.accountBalance}`,
      paymentType: PaymentTransactionType.DstvPayment,
      amount: `${amount}`,
      amountToPay: `${quote.quoteAmount}`,
      location: location,
      dueDate: quote.paymentDueDate as string,
      paymentMetaData,
      amountToPayOverride
    });
  }

  payChangePackage(fromPackage: Partial<Product>, toPackage: Partial<Product>, quote: Quote, location: string, paymentMetaData: PaymentMetaData) {
    return this.makePayment({
      accountNumber: quote?.financialAccountNumber
        ? `${quote.financialAccountNumber}`
        : quote.accountNumber
          ? `${quote.accountNumber}`
          : undefined,
      smartCardNumber: fromPackage.smartcardNumber,
      paymentType: PaymentTransactionType.DstvPayment,
      amount: `${quote.payableAmount}`,
      productUserKey: `${ProductUtils.GetServiceName(fromPackage?.serviceName)}`,
      productNamesPrices: `${toPackage.serviceName}:${toPackage.price}`,
      location: location,
      paymentMetaData,
    });
  }

  payAmount(account: Account, amount: string, location: string, paymentMetaData: PaymentMetaData) {
    return this.makePayment({
      accountNumber: `${account.accountNumber}`,
      accountBalance: `${account.accountBalance?.value}`,
      paymentType: PaymentTransactionType.DstvPayment,
      amount: `${amount}`,
      amountToPay: `${account.amountToPay?.value}`,
      location: location,
      dueDate: account.dueDate,
      paymentMetaData
    });
  }

  payBill(product: Partial<Product>, amount: string, location: string, paymentMetaData: PaymentMetaData) {
    return this.makePayment({
      smartCardNumber: product.smartcardNumber,
      productNamesPrices: `${product.serviceName}:${product.price}`,
      amount: amount,
      paymentType: PaymentTransactionType.DstvPayment,
      location: location,
      paymentMetaData
    });
  }

  payBoxoffice(account: Account, amount: string, balance: string, location: string, paymentMetaData: PaymentMetaData) {
    return this.makePayment({
      accountNumber: `${account.accountNumber}`,
      paymentType: PaymentTransactionType.BoxOffice,
      amount: `${this.currencyStore.getAsMainCurrency(amount)}`,
      accountBalance: balance,
      amountToPay: `${account.amountToPay?.value}`,
      location: location,
      paymentMetaData,
      dueDate: new Date().toJSON(),
    });
  }

  payAndTokenize(account: Account, amount: string, location: string, paymentMetaData: PaymentMetaData) {
    return this.makePayment(
      {
        accountNumber: `${account.accountNumber}`,
        accountBalance: `${account.accountBalance?.value}`,
        paymentType: PaymentTransactionType.DstvPayment,
        amount: amount, // TODO: may require conversion when MAH start tokenising payments
        amountToPay: `${account.amountToPay?.value}`,
        location: location,
        dueDate: account.dueDate,
        paymentMetaData
      },
      true
    );
  }

  startPoll(transactionId: string, type?: string) {
    this.pollingStore.startPoll(transactionId);
  }

  paymentFailed(reason: string) {
    this.pollingStore?.paymentFailed(reason);
  }

  paymentSuccess() {
    this.pollingStore?.paymentSuccess();
  }

  clearState = () => {
    this.clearPaymentContext();
    this.pollingStore.clearState();
  }

  private getPaymentService() {
    switch (this.paymentPlatform) {
      case PaymentPlatform.DPP:
        return new DPPaymentService();

      case PaymentPlatform.RapydRedirect:
        return new PSHPaymentService();

      case PaymentPlatform.RapydEmbedded:
        return new RapydPaymentService();

      case PaymentPlatform.PayAtExternal:
        return new PayAtLinkPaymentService();

      case PaymentPlatform.PayAtRedirect:
        return new PayAtPaymentService();

      case PaymentPlatform.PaymentRequestDirectLink:
        return new DirectLinkPaymentService();

      case PaymentPlatform.PaymentRequestMoment:
        return new MomentPaymentService();

      case PaymentPlatform.PaymentRequestMomentCheckout:
        return new MomentCheckoutPaymentService();
    }
  }

  private setPaymentService() {
    const service = this.getPaymentService();
    this.changePaymentService(service);
  }

  private setPaymentContext(request: any) {
    StorageHelper.GetInstance.setCache('sessions', request, CacheAreaKey.Payments, 'create')
  }

  private clearPaymentContext() {
    StorageHelper.GetInstance.removeFromStorage('sessions', CacheAreaKey.Payments)
  }
}